import React from "react";
import { ActionButtons, LoadingV2, AppToasterV2 } from "@ucl/library";
import { useUnderwritingRuleResults } from "@/hooks/useUnderwritingRuleResults";
import { UnderwritingRuleResultForm } from "@/components/UnderwritingRuleResultForm";
import { useAuthenticatedApiClient } from "@/hooks/useAuthenticatedApiClient";
import UnderwritingRuleResultsApiClient from "@/api/underwritingRuleResultsApiClient";
import "./UnderwritingRuleResultsTable.scss";

interface UnderwritingRuleResultsTableProps {
  parentEntityType: string;
  parentEntityId: string;
}

export const UnderwritingRuleResultsTable: React.FC<UnderwritingRuleResultsTableProps> = ({
  parentEntityType,
  parentEntityId,
}) => {
  const { ruleResults, loading, error, refetch } = useUnderwritingRuleResults({
    parentEntityType,
    parentEntityId,
  });
  
  const apiClient = useAuthenticatedApiClient(UnderwritingRuleResultsApiClient);

  const handleRunRules = async () => {
    try {
      await apiClient.runRules(parentEntityType, parentEntityId);
      // Refresh the data after running rules
      await refetch();
      // Show success toast
      AppToasterV2.showSuccessAppToaster(
        "Results have been updated.",
        "Rules Executed"
      );
    } catch (error) {
      console.error("Error running underwriting rules:", error);
      // Show error toast
      AppToasterV2.showFailedActionToaster(
        "Failed to execute underwriting rules. Please try again.",
        "Error"
      );
    }
  };

  if (loading) {
    return <LoadingV2 />;
  }

  if (error) {
    return (
      <div className="underwriting-rule-results-table">
        <div className="error-message" style={{ 
          padding: "20px", 
          backgroundColor: "#f8d7da", 
          border: "1px solid #f5c6cb", 
          borderRadius: "4px", 
          color: "#721c24" 
        }}>
          <p>Error loading underwriting rule results: {error}</p>
          <button 
            onClick={refetch} 
            style={{
              marginTop: "10px",
              padding: "8px 16px",
              backgroundColor: "#dc3545",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer"
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (ruleResults.length === 0) {
    return (
      <div className="underwriting-rule-results-table">
        <div className="no-results" style={{ 
          padding: "40px", 
          textAlign: "center", 
          color: "#6c757d" 
        }}>
          <p>No underwriting rule results found for this application.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="underwriting-rule-results-table" style={{ padding: "20px" }}>
      <div className="table-header" style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center", 
        marginBottom: "20px" 
      }}>
        <ActionButtons
          actions={[
            {
              text: "Run Rules",
              isPrimary: true,
              position: "right",
              onActionClick: handleRunRules,
            }
          ]}
        />
      </div>
      <div className="table-container" style={{ 
        backgroundColor: "white", 
        borderRadius: "8px", 
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        overflow: "hidden"
      }}>
        <table className="results-table" style={{ 
          width: "100%", 
          borderCollapse: "collapse",
          fontSize: "14px"
        }}>
          <thead>
            <tr style={{ backgroundColor: "#f8f9fa" }}>
              <th style={{ padding: "12px", textAlign: "left", fontWeight: "600", borderBottom: "2px solid #dee2e6" }}>Rule ID</th>
              <th style={{ padding: "12px", textAlign: "left", fontWeight: "600", borderBottom: "2px solid #dee2e6" }}>Entity Type</th>
              <th style={{ padding: "12px", textAlign: "left", fontWeight: "600", borderBottom: "2px solid #dee2e6" }}>Entity Display Name</th>
              <th style={{ padding: "12px", textAlign: "left", fontWeight: "600", borderBottom: "2px solid #dee2e6" }}>Product Name</th>
              <th style={{ padding: "12px", textAlign: "left", fontWeight: "600", borderBottom: "2px solid #dee2e6" }}>Description</th>
              <th style={{ padding: "12px", textAlign: "left", fontWeight: "600", borderBottom: "2px solid #dee2e6" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {ruleResults.map((result, index) => (
              <tr 
                key={result.id} 
                style={{ 
                  backgroundColor: index % 2 === 0 ? "white" : "#f8f9fa",
                  borderBottom: "1px solid #dee2e6"
                }}
              >
                <td style={{ padding: "12px", fontWeight: "500", color: "#495057" }}>
                  {result.ruleFriendlyId}
                </td>
                <td style={{ padding: "12px", color: "#6c757d" }}>
                  {result.entityType}
                </td>
                <td style={{ padding: "12px", color: "#495057" }}>
                  {result.entityDisplayName}
                </td>
                <td style={{ padding: "12px", color: "#495057" }}>
                  {result.productName || "N/A"}
                </td>
                <td style={{ padding: "12px", color: "#495057", maxWidth: "300px" }}>
                  <div 
                    style={{ 
                      overflow: "hidden", 
                      textOverflow: "ellipsis", 
                      whiteSpace: "nowrap"
                    }}
                    title={result.ruleDescription}
                  >
                    {result.ruleDescription}
                  </div>
                </td>
                <td style={{ padding: "12px" }}>
                  <UnderwritingRuleResultForm ruleResultId={result.id} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
